export const capitalize = (value: string): string => value.toLowerCase().replace(/^./, (char) => char.toUpperCase())

/**
 * @name sanitizePageDesignerHtml
 *
 * @description
 * Lightweight, SSR-safe sanitizer for the small amount of inline HTML that authors put in
 * Page Designer text fields (mainly `<br>`, plus occasional inline emphasis). Page Designer
 * escapes markup on save (e.g. `&lt;br&gt;`), which the SCAPI getPage response returns as a
 * literal `<br>` string; rendered as plain text it shows up verbatim instead of breaking the
 * line. This helper produces a string safe to pass to `dangerouslySetInnerHTML`.
 *
 * It is intentionally a whitelist: only a fixed set of inline formatting tags survive, every
 * other tag is stripped, and `<script>`/`<style>` bodies are removed entirely. It does NOT run
 * a full HTML parser (no DOMPurify dependency, works identically on server and client). Use it
 * only for trusted editorial copy coming from Page Designer, not for arbitrary user input.
 *
 * @param {string} [value] - The raw text/HTML string from a Page Designer field.
 * @returns {string} A sanitized HTML string containing only whitelisted inline tags.
 */
const ALLOWED_INLINE_TAGS = ['br', 'b', 'strong', 'i', 'em', 'u', 'sup', 'sub', 'span', 'a', 'p'] as const

export const sanitizePageDesignerHtml = (value?: string | null): string => {
    if (!value) return ''

    let html = String(value)

    // Drop entire <script>/<style> blocks (tag + contents), case-insensitive, across newlines.
    html = html.replace(/<\s*(script|style)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '')

    // Strip any tag that is not in the whitelist. Keeps the whitelisted tag (normalized to
    // lowercase, attributes preserved for <a>/<span> only) and removes everything else.
    html = html.replace(/<\s*\/?\s*([a-zA-Z][a-zA-Z0-9]*)\b([^>]*)>/g, (match, rawTag: string, attrs: string) => {
        const tag = rawTag.toLowerCase()
        if (!(ALLOWED_INLINE_TAGS as readonly string[]).includes(tag)) return ''

        const isClosing = /^<\s*\//.test(match)
        if (isClosing) return `</${tag}>`

        // For <a> keep only safe href/target/rel; for other tags drop all attributes.
        if (tag === 'a') {
            const hrefMatch = attrs.match(/\bhref\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i)
            const href = hrefMatch ? (hrefMatch[2] ?? hrefMatch[3] ?? hrefMatch[4] ?? '') : ''
            // Block javascript:/data: and other non-http(s) schemes; allow relative and http(s).
            const safeHref = /^\s*(https?:\/\/|\/|#|mailto:|tel:)/i.test(href) ? href : ''
            const rel = ' rel="noopener noreferrer"'
            return safeHref ? `<a href="${safeHref}"${rel}>` : '<a>'
        }

        return `<${tag}>`
    })

    return html
}
